/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import moment from "moment-timezone";
import "moment/locale/es";
import "moment/locale/fr";
import TextField from "@mui/material/TextField";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import lightColors from "assets/theme/base/colors";
import darkColors from "assets/theme-dark/base/colors";

// Material Dashboard 2 PRO React components
import { useMaterialUIController } from "context";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";

function FormDateTimePicker({ input, timeZone, ...rest }) {
  const { label } = rest;
  const pickerTimeZone = timeZone || "UTC";
  const mainUserLanguage = useSelector((state) => state.mainUserReducer?.language || "es");
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const activeColors = darkMode ? darkColors : lightColors;
  const pickerLocale = useMemo(() => {
    const normalizedLanguage = String(mainUserLanguage || "es").toLowerCase();
    return ["es", "en", "fr"].includes(normalizedLanguage) ? normalizedLanguage : "es";
  }, [mainUserLanguage]);

  const pickerTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? "dark" : "light",
          background: {
            default: activeColors.background?.default,
            paper: activeColors.background?.card || activeColors.background?.default,
          },
          text: {
            main: activeColors.text?.main,
            primary: activeColors.text?.main,
            secondary: activeColors.text?.focus || activeColors.text?.main,
          },
          white: {
            ...activeColors.white,
          },
          black: {
            ...activeColors.black,
          },
          info: {
            ...activeColors.info,
          },
        },
      }),
    [activeColors, darkMode]
  );

  const pickerPaperSx = useMemo(
    () => (theme) => {
      const surfaceColor =
        theme.palette.background?.paper ||
        theme.palette.background?.card ||
        theme.palette.background?.default;
      const textColor = theme.palette.text?.main || theme.palette.text?.primary;
      return {
        backgroundColor: surfaceColor,
        color: textColor,
      };
    },
    []
  );

  const pickerPopperSx = useMemo(
    () => (theme) => {
      const surfaceColor =
        theme.palette.background?.paper ||
        theme.palette.background?.card ||
        theme.palette.background?.default;
      const textColor = theme.palette.text?.main || theme.palette.text?.primary;
      const calendarHeaderColor = theme.palette.text?.main || theme.palette.text?.primary;
      const selectedTextColor = darkMode
        ? theme.palette.black?.main || theme.palette.common?.black
        : theme.palette.white?.main || theme.palette.common?.white;

      return {
        "& .MuiPickersPopper-paper, & .MuiDialog-paper, & .MuiPickersLayout-root": {
          backgroundColor: surfaceColor,
          color: textColor,
        },
        "& .MuiPickersCalendarHeader-root": {
          color: calendarHeaderColor,
        },
        "& .MuiPickersCalendarHeader-labelContainer": {
          display: "inline-flex !important",
          alignItems: "center",
          minWidth: "fit-content",
          opacity: "1 !important",
          visibility: "visible !important",
        },
        "& .MuiPickersCalendarHeader-label": {
          display: "inline-block !important",
          color: `${calendarHeaderColor} !important`,
          fontWeight: "600 !important",
          fontSize: "0.9rem !important",
          lineHeight: 1.15,
          opacity: "1 !important",
          visibility: "visible !important",
          textTransform: "capitalize",
        },
        "& .MuiPickersCalendarHeader-switchViewButton": {
          color: `${calendarHeaderColor} !important`,
          width: 24,
          height: 24,
          minWidth: 24,
          minHeight: 24,
          padding: 2,
          marginLeft: 2,
          borderRadius: "50%",
        },
        "& .MuiPickersCalendarHeader-switchViewIcon": {
          color: `${calendarHeaderColor} !important`,
          fontSize: "0.9rem",
        },
        "& .MuiPickersArrowSwitcher-root": {
          display: "inline-flex !important",
          alignItems: "center",
        },
        "& .MuiPickersArrowSwitcher-button": {
          color: `${calendarHeaderColor} !important`,
          width: 24,
          height: 24,
          minWidth: 24,
          minHeight: 24,
          padding: 2,
          borderRadius: "50%",
        },
        "& .MuiPickersArrowSwitcher-button .MuiSvgIcon-root": {
          fontSize: "0.95rem",
        },
        "& .MuiPickersLayout-contentWrapper": {
          backgroundColor: surfaceColor,
        },
        "& .MuiPickersToolbar-root": {
          backgroundColor: surfaceColor,
          color: textColor,
        },
        "& .MuiDateCalendar-root, & .MuiMultiSectionDigitalClock-root, & .MuiTimeClock-root": {
          backgroundColor: surfaceColor,
          color: textColor,
        },
        "& .MuiTypography-root": {
          color: textColor,
        },
        "& .MuiPickersDay-root": {
          color: textColor,
        },
        "& .MuiPickersDay-root.Mui-selected": {
          backgroundColor: theme.palette.info.main,
          color: selectedTextColor,
        },
        "& .MuiClockNumber-root": {
          color: textColor,
        },
        "& .MuiTimeClock-root .Mui-selected": {
          backgroundColor: theme.palette.info.main,
          color: selectedTextColor,
        },
        "& .MuiMultiSectionDigitalClockSection-item": {
          color: textColor,
        },
        "& .MuiMultiSectionDigitalClockSection-item.Mui-selected": {
          backgroundColor: theme.palette.info.main,
          color: selectedTextColor,
        },
        "& .MuiClockPointer-root, & .MuiClockPointer-thumb, & .MuiClock-pin": {
          backgroundColor: theme.palette.info.main,
          borderColor: theme.palette.info.main,
        },
        "& .MuiPickersLayout-root .MuiIconButton-root": {
          minHeight: "unset",
          padding: 2,
          borderRadius: "50%",
          backgroundImage: "none",
        },
      };
    },
    [darkMode]
  );

  const pickerValue = useMemo(() => {
    const rawValue = input?.value;
    if (!rawValue) return null;

    if (moment.isMoment(rawValue)) {
      if (!rawValue.isValid()) return null;
      return moment.tz ? rawValue.clone().tz(pickerTimeZone) : rawValue.clone();
    }

    const parsedValue = moment.tz ? moment.tz(rawValue, pickerTimeZone) : moment(rawValue);
    return parsedValue.isValid() ? parsedValue : null;
  }, [input?.value, pickerTimeZone]);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={pickerLocale}>
      <ThemeProvider theme={pickerTheme}>
        <DateTimePicker
          {...rest}
          format="DD/MM/YYYY HH:mm"
          slots={{
            textField: TextField,
            leftArrowIcon: ChevronLeftIcon,
            rightArrowIcon: ChevronRightIcon,
            switchViewIcon: ArrowDropDownIcon,
          }}
          slotProps={{
            textField: {
              variant: "standard",
              fullWidth: true,
            },
            popper: {
              sx: pickerPopperSx,
            },
            desktopPaper: {
              sx: pickerPaperSx,
            },
            mobilePaper: {
              sx: pickerPaperSx,
            },
          }}
          label={label}
          value={pickerValue}
          timezone={pickerTimeZone}
          onChange={(newValue) => {
            input.onChange(newValue && moment.isMoment(newValue) ? newValue.toDate() : null);
          }}
          ampm={false}
          ampmInClock={false}
        />
      </ThemeProvider>
    </LocalizationProvider>
  );
}

// Setting default values for the props of FormDatePîcker
FormDateTimePicker.defaultProps = {
  label: " ",
  input: {},
  meta: {
    touched: false,
    invalid: false,
    error: "",
  },
  timeZone: null,
};

// Typechecking props for FormDateTimePicker
FormDateTimePicker.propTypes = {
  label: PropTypes.string,
  input: PropTypes.object,
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    invalid: PropTypes.bool,
    error: PropTypes.string,
  }),
  timeZone: PropTypes.string,
};

export default FormDateTimePicker;
